import { yupResolver } from '@hookform/resolvers/yup'
import { Button, ErrorNotification, Modal, toaster } from '@mr-yum/frontend-ui'
import { InputField } from 'components/HookForm/InputField'
import { useLogger } from 'hooks/useLogger'
import { useUpdateReceiptEmailMutation } from 'lib/gql'
import React, { useCallback, useRef } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import * as Yup from 'yup'

interface Props {
  isOpen: boolean
  email?: string | null
  shouldNavigate?: boolean
  onClose(): void
  onSuccess?(): void
  venueSlug: string
}

export const receiptEmailDrawerSchema = ({ formatMessage }: IntlShape) =>
  Yup.object()
    .noUnknown(false)
    .shape({
      email: Yup.string()
        .trim()
        .email(
          formatMessage({
            id: 'qDmele',
            defaultMessage: 'Email must be valid',
          }),
        )
        .required(
          formatMessage({
            id: 'gtleS5',
            defaultMessage: 'Please provide your email address',
          }),
        ),
    })

type FormValues = {
  email: string
}

export const ReceiptEmailDrawer = ({
  onClose,
  onSuccess,
  isOpen,
  email,
  shouldNavigate = false,
  venueSlug,
}: Props) => {
  const { logEvent } = useLogger()
  const intl = useIntl()
  const form = useForm<FormValues>({
    defaultValues: {
      email: email ?? '',
    },
    mode: 'all',
    resolver: yupResolver(receiptEmailDrawerSchema(intl)),
  })
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form
  const initialFocusRef = useRef(null)
  const [{ error }, updateReceiptEmail] = useUpdateReceiptEmailMutation()

  const submitHandler = useCallback<SubmitHandler<FormValues>>(
    async ({ email }) => {
      logEvent('Updating email address', {
        venueSlug,
      })

      const { error } = await updateReceiptEmail({
        receiptEmail: email.trim(),
        venueSlug,
      })

      if (!error) {
        toaster.positive(
          <>
            <FormattedMessage
              defaultMessage="Successfully updated email address"
              id="3loOA+"
            />
          </>,
        )

        onClose?.()
        onSuccess?.()
      }

      return
    },
    [venueSlug, logEvent, updateReceiptEmail, onClose, onSuccess],
  )

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      initialFocusRef={initialFocusRef}
      title={
        email ? (
          <div className="my-label-md">
            <FormattedMessage
              defaultMessage="Change your email address"
              id="jCEOwx"
            />
          </div>
        ) : (
          <div className="my-label-md">
            <FormattedMessage
              defaultMessage="Add your email address"
              id="mP29w1"
            />
          </div>
        )
      }
    >
      <div className="p-4">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(submitHandler)}>
            {error && <ErrorNotification fullWidth error={error} />}

            <p className="mb-4 my-body-md">
              {email ? (
                <FormattedMessage
                  defaultMessage="Change your email address below to receive your future receipts."
                  id="VtsN7q"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Add your email address below to receive your future receipts."
                  id="5X2AT+"
                />
              )}
            </p>

            <InputField
              autoFocus
              data-testid="receiptEmail"
              name="email"
              label={intl.formatMessage({
                defaultMessage: 'Email address',
                id: 'hJZwTS',
              })}
            />

            <Button isLoading={isSubmitting} type="submit" size="lg" fullWidth>
              {shouldNavigate ? (
                <FormattedMessage defaultMessage="Get receipt" id="tFK1ck" />
              ) : email ? (
                <FormattedMessage defaultMessage="Change email" id="ChATeO" />
              ) : (
                <FormattedMessage defaultMessage="Add email" id="jdpGXt" />
              )}
            </Button>
          </form>
        </FormProvider>
      </div>
    </Modal>
  )
}
